import {motion, usePresence} from 'framer-motion';
import {cn, cva} from 'platform/shared';

import {forwardRef, MouseEvent, ReactNode, useEffect} from 'react';

import {useComponentsTheme} from '../../../hooks/useComponentsTheme';
import {DialogSize} from '../types/DialogSize';
import {dialogTransitions} from '../utils/dialogTransitions';

export interface DialogTransitionProps {
  size?: DialogSize;
  preset?: 'slideInBottom' | 'slideInRight' | 'scale' | 'none';
  children: ReactNode;
  isEnlarged?: boolean;
  scrollBehavior?: 'inside' | 'outside';
  disableMaxHeight?: boolean;
}

const dialogSectionVariants = cva(
  'relative flex w-full flex-col bg-[var(--color-palettes-white-10-100)] outline-0 [border-color:var(--color-palettes-neutral-40-100)] [box-shadow:var(--shadow-elevation-4)]',
  {
    variants: {
      size: {
        small: 'mx-[3.75rem] h-auto max-w-[400px] rounded-[var(--radius-medium)]',
        default: 'mx-[3.75rem] h-auto max-w-[800px] rounded-[var(--radius-medium)]',
        large: 'mx-[3.75rem] h-auto max-w-[1200px] rounded-[var(--radius-medium)]',
        xLarge: 'm-4 h-[calc(100%_-_2rem)] max-w-[2000px] rounded-[var(--radius-medium)]',
      },
      scrollBehavior: {
        inside: '',
        outside: 'my-auto',
      },
    },
    defaultVariants: {
      size: 'default',
      scrollBehavior: 'inside',
    },
  }
);

export const DialogTransition = forwardRef<HTMLElement, DialogTransitionProps>(
  function DialogTransition(props, ref) {
    const theme = useComponentsTheme();
    const motionProps = dialogTransitions[props.preset ?? 'none'];
    const [isPresent, safeToRemove] = usePresence();

    useEffect(
      function scheduleRemovalOnExit() {
        if (!isPresent && safeToRemove) {
          setTimeout(safeToRemove);
        }
      },
      [isPresent, safeToRemove]
    );

    const handleClick = (event: MouseEvent<HTMLElement>) => {
      event.stopPropagation();
    };

    const maxHeight = getDialogMaxHeight(props, theme);

    return (
      <motion.section
        ref={ref}
        onClick={handleClick}
        {...motionProps}
        className={cn(
          dialogSectionVariants({
            size: props.size ?? 'default',
            scrollBehavior: props.scrollBehavior ?? 'inside',
          })
        )}
        style={{maxHeight}}
      >
        {props.children}
      </motion.section>
    );
  }
);

function getDialogMaxHeight(
  props: DialogTransitionProps,
  theme: ReturnType<typeof useComponentsTheme>
) {
  if (props.scrollBehavior === 'outside' || props.size === 'xLarge' || props.disableMaxHeight) {
    return 'none';
  }

  return `calc(100% - ${theme.getSize(props.isEnlarged ? 8 : 30)})`;
}
